import { useState, useEffect } from "react";
import { X, LoaderCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card } from "./ui/card";

const API = import.meta.env.VITE_API_URL || "http://localhost:9000";

type Organization = {
  id: string;
  name: string;
};

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialOrganizationId?: string;
}

export function CreateUserModal({ isOpen, onClose, onSuccess, initialOrganizationId }: CreateUserModalProps) {
  const [loading, setLoading] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    isSuperAdmin: false,
    organizationId: initialOrganizationId || "",
    organizationRole: "member",
  });
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      fetch(`${API}/api/admin/organizations`, { credentials: "include" })
        .then((r) => r.json())
        .then((data) => setOrganizations(data))
        .catch((err) => console.error("Failed to fetch organizations", err));
    }
    if (initialOrganizationId) {
      setForm(prev => ({ ...prev, organizationId: initialOrganizationId }));
    }
  }, [isOpen, initialOrganizationId]);

  if (!isOpen) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API}/api/admin/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Kunne ikke opprette bruker");
      }

      onSuccess();
      onClose();
      // Reset form
      setForm({
        name: "",
        email: "",
        phoneNumber: "",
        isSuperAdmin: false,
        organizationId: initialOrganizationId || "",
        organizationRole: "member",
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <Card className="w-full max-w-lg shadow-2xl animate-in fade-in zoom-in duration-200">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-bold">Opprett ny bruker</h2>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-lg">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="name">Navn</Label>
            <Input
              id="name"
              required
              placeholder="Fullt navn"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-post</Label>
              <Input
                id="email"
                type="email"
                placeholder="bruker@eksempel.no"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Telefon</Label>
              <Input
                id="phone"
                placeholder="+47 123 45 678"
                value={form.phoneNumber}
                onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })}
              />
            </div>
          </div>

          <div className="flex items-center gap-2 py-2">
            <input
              type="checkbox"
              id="isSuperAdmin"
              className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
              checked={form.isSuperAdmin}
              onChange={(e) => setForm({ ...form, isSuperAdmin: e.target.checked })}
            />
            <Label htmlFor="isSuperAdmin" className="text-sm font-medium">Er Super Admin?</Label>
          </div>

          <div className="space-y-4 pt-4 border-t border-border">
            <div className="space-y-2">
              <Label htmlFor="org">Organisasjon (valgfritt)</Label>
              <select
                id="org"
                className="w-full h-10 px-3 py-2 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 appearance-none"
                value={form.organizationId}
                onChange={(e) => setForm({ ...form, organizationId: e.target.value })}
                disabled={!!initialOrganizationId}
              >
                <option value="">Ingen (kun systembruker)</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </div>

            {form.organizationId && (
              <div className="space-y-2">
                <Label htmlFor="role">Rolle i organisasjon</Label>
                <select
                  id="role"
                  className="w-full h-10 px-3 py-2 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 appearance-none"
                  value={form.organizationRole}
                  onChange={(e) => setForm({ ...form, organizationRole: e.target.value })}
                >
                  <option value="member">Bruker</option>
                  <option value="admin">Admin</option>
                  <option value="owner">Eier</option>
                </select>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Avbryt
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />}
              Opprett bruker
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
